import React from 'react';
import { Select, Input, Space } from 'antd';

// Modest curated set (this platform's current markets, same convention as
// HeavyDutyMachineryController's ValidTimezones / HOST_LANGUAGE_OPTIONS) rather
// than every ITU dial code.
const DIAL_CODES = [
  { code: '+34', flag: '🇪🇸' },
  { code: '+351', flag: '🇵🇹' },
  { code: '+33', flag: '🇫🇷' },
  { code: '+49', flag: '🇩🇪' },
  { code: '+39', flag: '🇮🇹' },
  { code: '+44', flag: '🇬🇧' },
  { code: '+1', flag: '🇺🇸' },
  { code: '+52', flag: '🇲🇽' },
  { code: '+57', flag: '🇨🇴' },
  { code: '+54', flag: '🇦🇷' },
  { code: '+56', flag: '🇨🇱' },
];

const DEFAULT_DIAL_CODE = '+34';

interface PhoneNumberInputProps {
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

/** Splits a combined "+34 673456789" value into [dialCode, number]. */
const splitValue = (value?: string): [string, string] => {
  if (!value) return [DEFAULT_DIAL_CODE, ''];
  const match = value.match(/^(\+\d{1,3})\s?(.*)$/);
  return match ? [match[1], match[2]] : [DEFAULT_DIAL_CODE, value];
};

/**
 * Country-code select + local number, combined into one "+34 673456789"
 * string on change -- same value/onChange contract as PhotoUpload/AvatarUpload,
 * so it drops into a Form.Item exactly like a plain Input would.
 */
const PhoneNumberInput: React.FC<PhoneNumberInputProps> = ({ value, onChange, disabled, placeholder }) => {
  const [dialCode, number] = splitValue(value);

  const emit = (nextDialCode: string, nextNumber: string) => {
    onChange?.(nextNumber ? `${nextDialCode} ${nextNumber}` : '');
  };

  return (
    <Space.Compact style={{ display: 'flex' }}>
      {/* Explicit height on both -- left to antd's default, the flag
          emoji's own font metrics make the Select render taller than the
          plain Input next to it, so the pair looks misaligned. */}
      <Select
        value={dialCode}
        disabled={disabled}
        style={{ width: 100, height: 40 }}
        onChange={(nextDialCode) => emit(nextDialCode, number)}
        options={DIAL_CODES.map((d) => ({ value: d.code, label: `${d.flag} ${d.code}` }))}
      />
      <Input
        value={number}
        disabled={disabled}
        placeholder={placeholder}
        style={{ flex: 1, height: 40 }}
        onChange={(e) => emit(dialCode, e.target.value)}
      />
    </Space.Compact>
  );
};

export default PhoneNumberInput;
